import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  Alert,
  Chip,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Security as SecurityIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { apiClient } from '../../services/api';

interface PriceGuardrail {
  id?: string;
  serviceCategory: string;
  minPrice?: number;
  maxPrice?: number;
  discountCapPercentage: number;
  discountDisabled: boolean;
}

interface ServiceRestriction {
  id?: string;
  serviceType: string;
  isAllowed: boolean;
  maxPriceOverride?: number;
}

interface Salon {
  id: string;
  name: string;
  salonType: string;
  brandProtectionLevel: string;
}

interface PriceGuardrailsManagerProps {
  salon: Salon;
  onClose: () => void;
}

const PriceGuardrailsManager: React.FC<PriceGuardrailsManagerProps> = ({ salon, onClose }) => {
  const [guardrails, setGuardrails] = useState<PriceGuardrail[]>([]);
  const [restrictions, setRestrictions] = useState<ServiceRestriction[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openGuardrailDialog, setOpenGuardrailDialog] = useState(false);
  const [openRestrictionDialog, setOpenRestrictionDialog] = useState(false);
  const [editingGuardrail, setEditingGuardrail] = useState<PriceGuardrail | null>(null);
  const [editingRestriction, setEditingRestriction] = useState<ServiceRestriction | null>(null);

  const [guardrailForm, setGuardrailForm] = useState<PriceGuardrail>({
    serviceCategory: '',
    minPrice: undefined,
    maxPrice: undefined,
    discountCapPercentage: 0,
    discountDisabled: false
  });

  const [restrictionForm, setRestrictionForm] = useState<ServiceRestriction>({
    serviceType: '',
    isAllowed: true,
    maxPriceOverride: undefined
  });

  const serviceCategories = [
    'BASIC_SERVICES',
    'MAKEUP',
    'BRIDAL_MAKEUP',
    'HAIR_STYLING',
    'FACIAL',
    'LUXURY_SERVICES',
    'PREMIUM_PACKAGES',
    'EXPERIMENTAL_SERVICES',
    'MASSAGE',
    'NAIL_SERVICES',
    'EYEBROW_THREADING',
    'WAXING',
    'SKIN_TREATMENTS'
  ];

  const serviceTypes = [
    'BRIDAL_MAKEUP',
    'LUXURY_FACIAL',
    'VIP_TREATMENT',
    'PREMIUM_HAIR_STYLING',
    'LUXURY_SERVICES',
    'PREMIUM_PACKAGES',
    'HIGH_END_TREATMENTS',
    'CELEBRITY_STYLING',
    'EXCLUSIVE_SERVICES'
  ];

  useEffect(() => {
    fetchData();
  }, [salon.id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [guardrailsResponse, restrictionsResponse] = await Promise.all([
        apiClient.get(`/tenants/${salon.id}/price-guardrails`),
        apiClient.get(`/tenants/${salon.id}/service-restrictions`)
      ]);

      if (guardrailsResponse.success) {
        setGuardrails(guardrailsResponse.data.guardrails || []);
      }

      if (restrictionsResponse.success) {
        setRestrictions(restrictionsResponse.data.restrictions || []);
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to fetch pricing data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGuardrails = async () => {
    try {
      setSaving(true);
      const response = await apiClient.put(`/tenants/${salon.id}/price-guardrails`, {
        guardrails
      });

      if (response.success) {
        setError(null);
      } else {
        setError('Failed to save price guardrails');
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to save price guardrails');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveRestrictions = async () => {
    try {
      setSaving(true);
      const response = await apiClient.put(`/tenants/${salon.id}/service-restrictions`, {
        restrictions
      });

      if (response.success) {
        setError(null);
      } else {
        setError('Failed to save service restrictions');
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to save service restrictions');
    } finally {
      setSaving(false);
    }
  };

  const handleAddGuardrail = () => {
    setGuardrailForm({
      serviceCategory: '',
      minPrice: undefined,
      maxPrice: undefined,
      discountCapPercentage: 0,
      discountDisabled: false
    });
    setEditingGuardrail(null);
    setOpenGuardrailDialog(true);
  };

  const handleEditGuardrail = (guardrail: PriceGuardrail) => {
    setGuardrailForm({ ...guardrail });
    setEditingGuardrail(guardrail);
    setOpenGuardrailDialog(true);
  };

  const handleSaveGuardrail = () => {
    if (editingGuardrail) {
      // Update existing
      setGuardrails(guardrails.map(g => 
        g.id === editingGuardrail.id ? { ...guardrailForm, id: editingGuardrail.id } : g
      ));
    } else {
      // Add new
      setGuardrails([...guardrails, { ...guardrailForm, id: Date.now().toString() }]);
    }
    setOpenGuardrailDialog(false);
  };

  const handleDeleteGuardrail = (id: string) => {
    setGuardrails(guardrails.filter(g => g.id !== id));
  };

  const handleAddRestriction = () => {
    setRestrictionForm({
      serviceType: '',
      isAllowed: true,
      maxPriceOverride: undefined
    });
    setEditingRestriction(null);
    setOpenRestrictionDialog(true);
  };

  const handleEditRestriction = (restriction: ServiceRestriction) => {
    setRestrictionForm({ ...restriction });
    setEditingRestriction(restriction);
    setOpenRestrictionDialog(true);
  };

  const handleSaveRestriction = () => {
    if (editingRestriction) {
      // Update existing
      setRestrictions(restrictions.map(r => 
        r.id === editingRestriction.id ? { ...restrictionForm, id: editingRestriction.id } : r
      ));
    } else {
      // Add new
      setRestrictions([...restrictions, { ...restrictionForm, id: Date.now().toString() }]);
    }
    setOpenRestrictionDialog(false);
  };

  const handleDeleteRestriction = (id: string) => {
    setRestrictions(restrictions.filter(r => r.id !== id));
  };

  const getSalonTypeColor = (salonType: string) => {
    switch (salonType) {
      case 'MAIN_BRAND': return 'primary';
      case 'SILENT_UTILITY': return 'warning';
      case 'EXPERIMENTAL': return 'info';
      case 'PARTNER': return 'secondary';
      default: return 'default';
    }
  };

  const getProtectionLevelColor = (level: string) => {
    switch (level) {
      case 'MAXIMUM': return 'error';
      case 'HIGH': return 'warning';
      case 'STANDARD': return 'success';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>Loading pricing governance...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h5" gutterBottom>
            Price Guardrails & Service Restrictions
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="subtitle1" color="text.secondary">
              {salon.name}
            </Typography>
            <Chip 
              label={salon.salonType.replace('_', ' ')} 
              color={getSalonTypeColor(salon.salonType) as any}
              size="small"
            />
            <Chip 
              label={`${salon.brandProtectionLevel} Protection`}
              color={getProtectionLevelColor(salon.brandProtectionLevel) as any}
              size="small"
              icon={<SecurityIcon />}
            />
          </Box>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {salon.salonType === 'SILENT_UTILITY' && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Silent Salon Pricing Policy
          </Typography>
          <Typography variant="body2">
            This salon has automatic price restrictions to maintain brand positioning. 
            Makeup services are capped at ₹1,500 and premium services are disabled.
          </Typography>
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Price Guardrails Section */}
        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">Price Guardrails</Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddGuardrail}
                  size="small"
                >
                  Add Guardrail
                </Button>
              </Box>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Category</TableCell>
                      <TableCell>Min Price</TableCell>
                      <TableCell>Max Price</TableCell>
                      <TableCell>Discount Cap</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {guardrails.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <Typography color="text.secondary">
                            No price guardrails configured
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      guardrails.map((guardrail) => (
                        <TableRow key={guardrail.id}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {guardrail.serviceCategory.replace('_', ' ')}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {guardrail.minPrice ? `₹${guardrail.minPrice}` : '-'}
                          </TableCell>
                          <TableCell>
                            {guardrail.maxPrice ? `₹${guardrail.maxPrice}` : '-'}
                          </TableCell>
                          <TableCell>
                            {guardrail.discountDisabled ? (
                              <Chip label="Disabled" color="error" size="small" />
                            ) : (
                              <Chip label={`${guardrail.discountCapPercentage}%`} color="primary" size="small" />
                            )}
                          </TableCell>
                          <TableCell>
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => handleEditGuardrail(guardrail)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton size="small" color="error" onClick={() => handleDeleteGuardrail(guardrail.id!)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ mt: 2, textAlign: 'right' }}>
                <Button
                  variant="contained"
                  onClick={handleSaveGuardrails}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Guardrails'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Service Restrictions Section */}
        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">Service Restrictions</Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddRestriction}
                  size="small"
                >
                  Add Restriction
                </Button>
              </Box>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Service Type</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Max Price</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {restrictions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography color="text.secondary">
                            No service restrictions configured
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      restrictions.map((restriction) => (
                        <TableRow key={restriction.id}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {restriction.serviceType.replace('_', ' ')}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {restriction.isAllowed ? (
                              <Chip 
                                label="Allowed" 
                                color="success" 
                                size="small" 
                                icon={<CheckCircleIcon />}
                              />
                            ) : (
                              <Chip 
                                label="Blocked" 
                                color="error" 
                                size="small" 
                                icon={<WarningIcon />}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            {restriction.maxPriceOverride ? `₹${restriction.maxPriceOverride}` : '-'}
                          </TableCell>
                          <TableCell>
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => handleEditRestriction(restriction)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton size="small" color="error" onClick={() => handleDeleteRestriction(restriction.id!)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ mt: 2, textAlign: 'right' }}>
                <Button
                  variant="contained"
                  onClick={handleSaveRestrictions}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Restrictions'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button onClick={onClose}>
          Close
        </Button>
      </Box>

      {/* Guardrail Dialog */}
      <Dialog open={openGuardrailDialog} onClose={() => setOpenGuardrailDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingGuardrail ? 'Edit Price Guardrail' : 'Add Price Guardrail'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Service Category</InputLabel>
                <Select
                  value={guardrailForm.serviceCategory}
                  onChange={(e) => setGuardrailForm({ ...guardrailForm, serviceCategory: e.target.value })}
                >
                  {serviceCategories.map(category => (
                    <MenuItem key={category} value={category}>
                      {category.replace('_', ' ')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Minimum Price (₹)"
                type="number"
                value={guardrailForm.minPrice || ''}
                onChange={(e) => setGuardrailForm({ 
                  ...guardrailForm, 
                  minPrice: e.target.value ? Number(e.target.value) : undefined 
                })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Maximum Price (₹)"
                type="number"
                value={guardrailForm.maxPrice || ''}
                onChange={(e) => setGuardrailForm({ 
                  ...guardrailForm, 
                  maxPrice: e.target.value ? Number(e.target.value) : undefined 
                })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Discount Cap (%)"
                type="number"
                value={guardrailForm.discountCapPercentage}
                onChange={(e) => setGuardrailForm({ 
                  ...guardrailForm, 
                  discountCapPercentage: Number(e.target.value) 
                })}
                disabled={guardrailForm.discountDisabled}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={guardrailForm.discountDisabled}
                    onChange={(e) => setGuardrailForm({ 
                      ...guardrailForm, 
                      discountDisabled: e.target.checked,
                      discountCapPercentage: e.target.checked ? 0 : guardrailForm.discountCapPercentage
                    })}
                  />
                }
                label="Disable Discounts"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenGuardrailDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveGuardrail} variant="contained">
            {editingGuardrail ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Restriction Dialog */}
      <Dialog open={openRestrictionDialog} onClose={() => setOpenRestrictionDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingRestriction ? 'Edit Service Restriction' : 'Add Service Restriction'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Service Type</InputLabel>
                <Select
                  value={restrictionForm.serviceType}
                  onChange={(e) => setRestrictionForm({ ...restrictionForm, serviceType: e.target.value })}
                >
                  {serviceTypes.map(type => (
                    <MenuItem key={type} value={type}>
                      {type.replace('_', ' ')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={restrictionForm.isAllowed}
                    onChange={(e) => setRestrictionForm({ 
                      ...restrictionForm, 
                      isAllowed: e.target.checked 
                    })}
                  />
                }
                label="Service Allowed"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Max Price Override (₹)"
                type="number"
                value={restrictionForm.maxPriceOverride || ''}
                onChange={(e) => setRestrictionForm({ 
                  ...restrictionForm, 
                  maxPriceOverride: e.target.value ? Number(e.target.value) : undefined 
                })}
                disabled={!restrictionForm.isAllowed}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRestrictionDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveRestriction} variant="contained">
            {editingRestriction ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PriceGuardrailsManager;